import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  SimpleGrid,
  Paper,
  RingProgress,
  ThemeIcon,
  Divider,
} from '@mantine/core';
import {
  IconArrowUp,
  IconArrowDown,
  IconMinus,
} from '@tabler/icons-react';
import { Card, Title } from '../../ui';
import { FadeIn, StaggeredList } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface SummaryItem {
  key: string;
  label: string;
  value: string | number;
  previousValue?: number;
  change?: number;
  icon?: ReactNode;
  color?: string;
  format?: 'number' | 'currency' | 'percentage' | 'custom';
  description?: string;
}

export interface SummarySection {
  title: string;
  items: SummaryItem[];
}

export interface DataSummaryProps {
  title?: string;
  description?: string;
  items?: SummaryItem[];
  sections?: SummarySection[];
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  variant?: 'cards' | 'list' | 'compact';
  showChange?: boolean;
  showIcons?: boolean;
  className?: string;
}

export const DataSummary = forwardRef<HTMLDivElement, DataSummaryProps>(
  (
    {
      title,
      description,
      items = [],
      sections = [],
      columns = 4,
      variant = 'cards',
      showChange = true,
      showIcons = true,
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const formatValue = (item: SummaryItem): string => {
      const val = item.value;
      switch (item.format) {
        case 'currency':
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
          }).format(Number(val));
        case 'percentage':
          return `${val}%`;
        case 'number':
          return new Intl.NumberFormat('en-US').format(Number(val));
        default:
          return String(val);
      }
    };

    const getChangeColor = (change?: number) => {
      if (!change) return 'gray';
      return change > 0 ? 'green' : change < 0 ? 'red' : 'gray';
    };

    const getChangeIcon = (change?: number) => {
      if (!change) return <IconMinus size={12} />;
      return change > 0 ? <IconArrowUp size={12} /> : <IconArrowDown size={12} />;
    };

    const formatChange = (change?: number) => {
      if (!change) return '0%';
      const sign = change > 0 ? '+' : '';
      return `${sign}${change.toFixed(1)}%`;
    };

    const renderCardItem = (item: SummaryItem) => (
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" c="dimmed" style={accessibilityStyles}>{item.label}</Text>
            {showIcons && item.icon && (
              <ThemeIcon size="sm" variant="light" color={item.color || 'blue'} radius="md">
                {item.icon}
              </ThemeIcon>
            )}
          </Group>
          <Text size="xl" fw={700} style={accessibilityStyles}>{formatValue(item)}</Text>
          {showChange && item.change !== undefined && (
            <Group gap={4}>
              <ThemeIcon size="xs" color={getChangeColor(item.change)} variant="light" radius="xl">
                {getChangeIcon(item.change)}
              </ThemeIcon>
              <Text size="xs" c={getChangeColor(item.change)} fw={500} style={accessibilityStyles}>
                {formatChange(item.change)}
              </Text>
              {item.description && (
                <Text size="xs" c="dimmed" style={accessibilityStyles}>{item.description}</Text>
              )}
            </Group>
          )}
        </Stack>
      </Paper>
    );

    const renderListItem = (item: SummaryItem) => (
      <Group justify="space-between" py="xs">
        <Group gap="sm">
          {showIcons && item.icon && (
            <ThemeIcon size="sm" variant="light" color={item.color || 'blue'} radius="md">
              {item.icon}
            </ThemeIcon>
          )}
          <Box>
            <Text size="sm" style={accessibilityStyles}>{item.label}</Text>
            {item.description && (
              <Text size="xs" c="dimmed" style={accessibilityStyles}>{item.description}</Text>
            )}
          </Box>
        </Group>
        <Group gap="sm">
          <Text fw={600} style={accessibilityStyles}>{formatValue(item)}</Text>
          {showChange && item.change !== undefined && (
            <Group gap={2}>
              {getChangeIcon(item.change)}
              <Text size="xs" c={getChangeColor(item.change)} fw={500} style={accessibilityStyles}>
                {formatChange(item.change)}
              </Text>
            </Group>
          )}
        </Group>
      </Group>
    );

    const renderCompactItem = (item: SummaryItem) => (
      <Group gap="xs">
        {showIcons && item.icon && (
          <ThemeIcon size="xs" variant="light" color={item.color || 'blue'} radius="xl">
            {item.icon}
          </ThemeIcon>
        )}
        <Text size="sm" c="dimmed" style={accessibilityStyles}>{item.label}:</Text>
        <Text size="sm" fw={600} style={accessibilityStyles}>{formatValue(item)}</Text>
        {showChange && item.change !== undefined && (
          <Text size="xs" c={getChangeColor(item.change)} style={accessibilityStyles}>
            ({formatChange(item.change)})
          </Text>
        )}
      </Group>
    );

    const renderItems = (itemList: SummaryItem[]) => {
      if (variant === 'cards') {
        return (
          <SimpleGrid cols={{ base: 1, xs: 2, sm: Math.min(columns, 3), md: columns }}>
            {itemList.map((item) => (
              <Box key={item.key}>{renderCardItem(item)}</Box>
            ))}
          </SimpleGrid>
        );
      }

      if (variant === 'list') {
        return (
          <Stack gap={0}>
            {itemList.map((item, index) => (
              <Box key={item.key}>
                {renderListItem(item)}
                {index < itemList.length - 1 && <Divider />}
              </Box>
            ))}
          </Stack>
        );
      }

      return (
        <Group gap="lg">
          {itemList.map((item) => (
            <Box key={item.key}>{renderCompactItem(item)}</Box>
          ))}
        </Group>
      );
    };

    return (
      <FadeIn>
        <Card ref={ref} className={className}>
          <Stack gap="lg">
            {(title || description) && (
              <Box>
                {title && <Title order={4}>{title}</Title>}
                {description && <Text size="sm" c="dimmed" style={accessibilityStyles}>{description}</Text>}
              </Box>
            )}

            {/* Main items */}
            {items.length > 0 && (
              <StaggeredList staggerDelay={50}>
                {renderItems(items)}
              </StaggeredList>
            )}

            {/* Sections */}
            {sections.map((section, index) => (
              <Box key={section.title}>
                {index > 0 && <Divider my="md" />}
                <Text size="sm" fw={600} mb="sm" style={accessibilityStyles}>{section.title}</Text>
                <StaggeredList staggerDelay={50}>
                  {renderItems(section.items)}
                </StaggeredList>
              </Box>
            ))}
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

DataSummary.displayName = 'DataSummary';

export default DataSummary;
